import { InputHTMLAttributes, forwardRef } from 'react';
import { classNames } from '@/utils/classNames';

interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface RadioProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'checked'> {
  options: RadioOption[];
  label?: string;
  error?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  orientation?: 'horizontal' | 'vertical';
  value?: string;
  _checked?: boolean;
}

interface SingleRadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: string;
  description?: string;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
}

const SingleRadio = forwardRef<HTMLInputElement, SingleRadioProps>(
  ({ label, description, error, size = 'md', className, ...props }, ref) => {
    const sizeClasses = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    };

    const textSizeClasses = {
      sm: 'text-sm',
      md: 'text-sm',
      lg: 'text-base',
    };

    const radioClasses = classNames(
      'border-gray-300 dark:border-gray-600',
      'bg-white dark:bg-gray-700',
      'text-primary-600 focus:ring-primary-500',
      'transition-colors duration-200',
      'focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800',
      'touch-target cursor-pointer',
      sizeClasses[size],
      error && 'border-red-300 dark:border-red-600 focus:ring-red-500',
      props.disabled && 'opacity-50 cursor-not-allowed',
      className
    );

    const ContainerComponent = label || description ? 'label' : 'div';

    return (
      <ContainerComponent
        className={classNames(
          'flex items-start space-x-3',
          (label || description) && 'cursor-pointer touch-target',
          props.disabled && 'cursor-not-allowed'
        )}
      >
        <input ref={ref} type='radio' className={radioClasses} {...props} />
        {(label || description) && (
          <div className='flex-1 min-w-0'>
            {label && (
              <div
                className={classNames(
                  'font-medium',
                  textSizeClasses[size],
                  error ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300',
                  props.disabled && 'text-gray-500 dark:text-gray-500'
                )}
              >
                {label}
              </div>
            )}
            {description && (
              <p
                className={classNames(
                  'text-sm text-gray-500 dark:text-gray-400',
                  label && 'mt-1',
                  props.disabled && 'text-gray-400 dark:text-gray-600'
                )}
              >
                {description}
              </p>
            )}
          </div>
        )}
      </ContainerComponent>
    );
  }
);

const RadioGroup = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      options,
      label,
      error,
      helperText,
      size = 'md',
      orientation = 'vertical',
      name,
      onChange,
      value,
      _checked,
      ...props
    },
    ref
  ) => {
    const containerClasses = classNames(
      'space-y-3',
      orientation === 'horizontal' && 'flex flex-wrap gap-4 space-y-0'
    );

    return (
      <div className='space-y-2'>
        {label && (
          <legend
            className={classNames(
              'block text-sm font-medium',
              error ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'
            )}
          >
            {label}
          </legend>
        )}
        <div className={containerClasses}>
          {options.map((option, index) => (
            <SingleRadio
              key={option.value}
              ref={index === 0 ? ref : undefined}
              name={name}
              value={option.value}
              label={option.label}
              description={option.description}
              disabled={option.disabled || props.disabled}
              size={size}
              error={error}
              checked={value === option.value}
              onChange={onChange}
              {...props}
            />
          ))}
        </div>
        {error && <p className='text-sm text-red-600 dark:text-red-400'>{error}</p>}
        {helperText && !error && (
          <p className='text-sm text-gray-500 dark:text-gray-400'>{helperText}</p>
        )}
      </div>
    );
  }
);

SingleRadio.displayName = 'SingleRadio';
RadioGroup.displayName = 'RadioGroup';

const Radio = {
  Single: SingleRadio,
  Group: RadioGroup,
};

export default Radio;
